import { useQuery } from "@tanstack/react-query";
import { followUpsApi, Thread } from "@/app/lib/api/followUps";
import {
  FollowUpCampaign,
  FollowUpCommunication,
  FollowUpEntityType,
} from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { useAuth } from "@/app/lib/contexts/AuthContext";

export const followUpQueryKeys = {
  all: ["followUps"] as const,
  campaigns: {
    all: () => [...followUpQueryKeys.all, "campaigns"] as const,
    byCompany: (companyId: string) =>
      [...followUpQueryKeys.campaigns.all(), "company", companyId] as const,
    byEntity: (entityType: FollowUpEntityType, entityId: string) =>
      [...followUpQueryKeys.campaigns.all(), "entity", entityType, entityId] as const,
    detail: (campaignId: string) =>
      [...followUpQueryKeys.campaigns.all(), "detail", campaignId] as const,
  },
  communications: {
    all: () => [...followUpQueryKeys.all, "communications"] as const,
    byCompany: (companyId: string) =>
      [...followUpQueryKeys.communications.all(), "company", companyId] as const,
    byCampaign: (campaignId: string) =>
      [...followUpQueryKeys.communications.all(), "campaign", campaignId] as const,
    byThread: (threadId: string) =>
      [...followUpQueryKeys.communications.all(), "thread", threadId] as const,
    detail: (communicationId: string) =>
      [...followUpQueryKeys.communications.all(), "detail", communicationId] as const,
  },
  threads: {
    all: () => [...followUpQueryKeys.all, "threads"] as const,
    byCompany: (companyId: string) =>
      [...followUpQueryKeys.threads.all(), "company", companyId] as const,
  },
};

/**
 * Hook to fetch all follow-up campaigns for the current company
 */
export function useFollowUpCampaigns() {
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  return useQuery<FollowUpCampaign[]>({
    queryKey: followUpQueryKeys.campaigns.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return followUpsApi.getCampaigns(selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch follow-up campaigns for a specific entity (RFQ or Order)
 */
export function useFollowUpCampaignsByEntity(
  entityType: FollowUpEntityType,
  entityId: string
) {
  return useQuery<FollowUpCampaign[]>({
    queryKey: followUpQueryKeys.campaigns.byEntity(entityType, entityId),
    queryFn: () => followUpsApi.getCampaignsByEntity(entityType, entityId),
    enabled: !!entityId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single follow-up campaign
 */
export function useFollowUpCampaign(campaignId: string) {
  return useQuery<FollowUpCampaign>({
    queryKey: followUpQueryKeys.campaigns.detail(campaignId),
    queryFn: () => followUpsApi.getCampaign(campaignId),
    enabled: !!campaignId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch communications for a campaign
 */
export function useFollowUpCommunicationsByCampaign(campaignId: string) {
  return useQuery<FollowUpCommunication[]>({
    queryKey: followUpQueryKeys.communications.byCampaign(campaignId),
    queryFn: () => followUpsApi.getCommunicationsByCampaign(campaignId),
    enabled: !!campaignId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch communications for a thread
 */
export function useFollowUpCommunicationsByThread(threadId: string) {
  return useQuery<FollowUpCommunication[]>({
    queryKey: followUpQueryKeys.communications.byThread(threadId),
    queryFn: () => followUpsApi.getCommunicationsByThread(threadId),
    enabled: !!threadId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch all communications for a company
 */
export function useFollowUpCommunications() {
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  return useQuery<FollowUpCommunication[]>({
    queryKey: followUpQueryKeys.communications.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return followUpsApi.getCommunications(selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch threads with last message
 */
export function useFollowUpThreads() {
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  return useQuery<Thread[]>({
    queryKey: followUpQueryKeys.threads.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return followUpsApi.getThreadsWithLastMessage(selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single communication
 */
export function useFollowUpCommunication(communicationId: string) {
  return useQuery<FollowUpCommunication>({
    queryKey: followUpQueryKeys.communications.detail(communicationId),
    queryFn: () => followUpsApi.getCommunication(communicationId),
    enabled: !!communicationId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch follow-up campaigns for multiple entities
 * Returns a Map of entity_id to campaigns array
 */
export function useFollowUpCampaignsByEntityIds(entityIds: string[]) {
  const { selectedCompanyId } = useAppStore();
  const { user } = useAuth();

  return useQuery<Map<string, FollowUpCampaign[]>>({
    queryKey: [
      ...followUpQueryKeys.campaigns.all(),
      "bulk",
      selectedCompanyId,
      entityIds.sort().join(",")
    ],
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return followUpsApi.getCampaignsByEntityIds(entityIds, selectedCompanyId);
    },
    enabled: !!selectedCompanyId && !!user && entityIds.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}